import { useState, useRef, ChangeEvent, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import trackingService from '../services/trackingService';

export default function CadastroEstudante() {
  const navigate = useNavigate();

  // Rastrear início do cadastro
  useEffect(() => {
    trackingService.trackStartRegistration('student');
  }, []);
  
  // Estados para dados do usuário
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  
  // Estados para dados acadêmicos (ETAPA 2)
  const [cpf, setCpf] = useState('');
  const [instituicaoEnsino, setInstituicaoEnsino] = useState('');
  const [nomeCurso, setNomeCurso] = useState('');
  const [semestre, setSemestre] = useState('');
  const [periodo, setPeriodo] = useState<'matutino' | 'vespertino' | 'noturno' | 'integral' | ''>('');
  const [previsaoConclusao, setPrevisaoConclusao] = useState('');
  const [observacoes, setObservacoes] = useState('');

  // Estados para dados de login (ETAPA 3)
  const [empresaLogin, setEmpresaLogin] = useState(''); // Nome da clínica/prática para login
  const [nomeUsuario, setNomeUsuario] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmaSenha, setConfirmaSenha] = useState('');

  // Estado para foto de perfil
  const [fotoPerfil, setFotoPerfil] = useState<string | null>(null);
  const [fotoNome, setFotoNome] = useState('');

  // Estados de controle
  const [etapa, setEtapa] = useState(1);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [carregando, setCarregando] = useState(false);
  const [mensagemSucesso, setMensagemSucesso] = useState('');
  
  // Referência para o input de arquivo
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Validar etapa 1 (dados pessoais)
  const validarEtapa1 = (): boolean => {
    const novosErros: Record<string, string> = {};
    
    if (!nome.trim()) novosErros.nome = 'Nome completo é obrigatório';
    if (!email.trim()) novosErros.email = 'E-mail é obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) 
      novosErros.email = 'E-mail inválido';
    
    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };
  
  // Validar etapa 2 (dados acadêmicos)
  const validarEtapa2 = (): boolean => {
    const novosErros: Record<string, string> = {};
    
    if (!instituicaoEnsino.trim()) novosErros.instituicaoEnsino = 'Instituição de ensino é obrigatória';
    if (!nomeCurso.trim()) novosErros.nomeCurso = 'Nome do curso é obrigatório';
      
    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  // Validar etapa 3 (dados de login)
  const validarEtapa3 = (): boolean => {
    const novosErros: Record<string, string> = {};
    
    if (!empresaLogin.trim()) novosErros.empresaLogin = 'Nome da clínica/prática é obrigatório';
    else if (empresaLogin.length < 3) novosErros.empresaLogin = 'Nome da clínica/prática deve ter pelo menos 3 caracteres';
    
    if (!nomeUsuario.trim()) novosErros.nomeUsuario = 'Nome de usuário é obrigatório';
    if (!senha) novosErros.senha = 'Senha é obrigatória';
    else if (senha.length < 6) novosErros.senha = 'Senha deve ter pelo menos 6 caracteres';
    if (senha !== confirmaSenha) novosErros.confirmaSenha = 'Senhas não conferem';
    
    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };
  
  // Avançar para próxima etapa
  const avancarEtapa = () => {
    if (etapa === 1 && validarEtapa1()) {
      setEtapa(2);
      setErros({});
    } else if (etapa === 2 && validarEtapa2()) {
      setEtapa(3);
      setErros({});
    }
  };
  
  // Voltar para etapa anterior
  const voltarEtapa = () => {
    setEtapa(etapa - 1);
    setErros({});
  };

  // Função para ler arquivo como base64
  const lerComoBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
      reader.readAsDataURL(file);
    });
  };

  // Handler para mudança de foto
  const handleFotoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Verificar o tamanho do arquivo (limitar a 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setErros(prev => ({ ...prev, foto: 'A foto deve ter no máximo 2MB' }));
        return;
      }

      // Verificar o tipo do arquivo
      if (!file.type.startsWith('image/')) {
        setErros(prev => ({ ...prev, foto: 'Apenas arquivos de imagem são permitidos' }));
        return;
      }

      try {
        const base64 = await lerComoBase64(file);
        setFotoPerfil(base64);
        setFotoNome(file.name);
        
        // Limpar erro se houver
        const novosErros = { ...erros };
        delete novosErros.foto;
        setErros(novosErros);
      } catch (error) {
        console.error('Erro ao converter imagem:', error);
        setErros(prev => ({ ...prev, foto: 'Erro ao processar a imagem' }));
      }
    }
  };

  // Handler para submissão do formulário
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validarEtapa3()) return;
    
    try {
      setCarregando(true);
      
      const dadosCadastro = {
        nome,
        email,
        nome_usuario: nomeUsuario,
        senha,
        telefone: telefone || undefined,
        foto_perfil: fotoPerfil || undefined,
        empresa_fantasia: empresaLogin, // Nome da clínica/prática (empresa_login)
        cpf: cpf || undefined,
        instituicao_ensino: instituicaoEnsino,
        nome_curso: nomeCurso,
        semestre: semestre || undefined,
        periodo: periodo || undefined,
        previsao_conclusao: previsaoConclusao || undefined,
        observacoes: observacoes || undefined
      };
      
      console.log('[CADASTRO-ESTUDANTE] Enviando dados:', dadosCadastro);
      
      const response = await api.post('/estudantes/cadastrar', dadosCadastro);
      
      if (response.data.sucesso) {
        // Rastrear cadastro completo
        trackingService.trackRegistrationComplete({
          type: 'student',
          planName: 'Estudante Gratuito',
          trialDays: 0 // Estudante é gratuito
        });
        
        setMensagemSucesso('Cadastro realizado com sucesso! Redirecionando para o login...');
        
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setErros({ geral: response.data.mensagem || 'Erro ao cadastrar estudante' });
      }
      
    } catch (error: any) {
      console.error('[CADASTRO-ESTUDANTE] Erro ao cadastrar:', error);
      setErros({ 
        geral: error.response?.data?.mensagem || 
               'Erro ao realizar cadastro. Tente novamente.' 
      });
    } finally {
      setCarregando(false);
    }
  };

  return (
    <div className="register-page">
      <div className="register-box">
        <div className="card">
          <div className="card-header text-center">
            <img src="/img/logo-principal.png" alt="Logo" style={{ height: '60px', marginBottom: '10px' }} />
            <h1 className="h3">Cadastro de Estudante</h1>
            <p className="text-muted">Psicologia - Estágio Supervisionado</p>
          </div>
          <div className="card-body register-card-body">
            {mensagemSucesso ? (
              <div className="alert alert-success text-center">
                <h4>Cadastro realizado com sucesso!</h4>
                <p>{mensagemSucesso}</p>
              </div>
            ) : (
              <>
                {/* Indicador de progresso */}
                <div className="progress mb-3" style={{ height: '6px' }}>
                  <div 
                    className="progress-bar" 
                    style={{ width: `${(etapa / 3) * 100}%` }}
                  ></div>
                </div>
                <p className="text-center text-muted">Etapa {etapa} de 3</p>

                {erros.geral && (
                  <div className="alert alert-danger">
                    {erros.geral}
                  </div>
                )}

                {Object.keys(erros).length > 1 && (
                  <div className="alert alert-danger">
                    <ul className="mb-0">
                      {Object.entries(erros).filter(([key]) => key !== 'geral').map(([key, erro]) => (
                        <li key={key}>{erro}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <form onSubmit={(e) => { 
                  e.preventDefault(); 
                  if (etapa < 3) {
                    avancarEtapa();
                  } else {
                    handleSubmit(e);
                  }
                }}>
                  {/* ETAPA 1: Dados Pessoais */}
                  {etapa === 1 && (
                    <>
                      <h5 className="mb-3">Dados Pessoais</h5>
                      
                      <div className="mb-3">
                        <div className="input-group">
                          <input
                            type="text"
                            className={`form-control ${erros.nome ? 'is-invalid' : ''}`}
                            placeholder="Nome completo"
                            value={nome}
                            onChange={(e) => setNome(e.target.value)}
                            required
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-user"></i>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="input-group">
                          <input
                            type="email"
                            className={`form-control ${erros.email ? 'is-invalid' : ''}`}
                            placeholder="E-mail"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-envelope"></i>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="input-group">
                          <input
                            type="tel"
                            className="form-control"
                            placeholder="Telefone (opcional)"
                            value={telefone}
                            onChange={(e) => setTelefone(e.target.value)}
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-phone"></i>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="CPF (opcional)"
                            value={cpf}
                            onChange={(e) => setCpf(e.target.value)}
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-id-card"></i>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Campo para foto de perfil */}
                      <div className="mb-3">
                        <label className="form-label" htmlFor="cadastroEstudanteFotoPerfil">Foto de Perfil (opcional)</label>
                        <input
                          ref={fileInputRef}
                          id="cadastroEstudanteFotoPerfil"
                          type="file"
                          className={`form-control ${erros.foto ? 'is-invalid' : ''}`}
                          accept="image/*"
                          onChange={handleFotoChange}
                        />
                        {erros.foto && <div className="invalid-feedback">{erros.foto}</div>}
                        {fotoPerfil && (
                          <div className="mt-2">
                            <img 
                              src={fotoPerfil} 
                              alt="Preview" 
                              style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '50%' }}
                            />
                            <small className="text-muted ml-2">{fotoNome}</small>
                          </div>
                        )}
                      </div>

                      <button type="submit" className="btn btn-primary btn-block">
                        Continuar <i className="fas fa-arrow-right ml-2"></i>
                      </button>
                    </>
                  )}

                  {/* ETAPA 2: Dados Acadêmicos */}
                  {etapa === 2 && (
                    <>
                      <h5 className="mb-3">Dados Acadêmicos</h5>
                      
                      <div className="mb-3">
                        <div className="input-group">
                          <input
                            type="text"
                            className={`form-control ${erros.instituicaoEnsino ? 'is-invalid' : ''}`}
                            placeholder="Instituição de ensino"
                            value={instituicaoEnsino}
                            onChange={(e) => setInstituicaoEnsino(e.target.value)}
                            required
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-university"></i>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="input-group">
                          <input
                            type="text"
                            className={`form-control ${erros.nomeCurso ? 'is-invalid' : ''}`}
                            placeholder="Nome do curso"
                            value={nomeCurso}
                            onChange={(e) => setNomeCurso(e.target.value)}
                            required
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-graduation-cap"></i>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Semestre (opcional)"
                            value={semestre}
                            onChange={(e) => setSemestre(e.target.value)}
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <select
                            className="form-control"
                            value={periodo}
                            onChange={(e) => setPeriodo(e.target.value as any)}
                          >
                            <option value="">Período (opcional)</option>
                            <option value="matutino">Matutino</option>
                            <option value="vespertino">Vespertino</option>
                            <option value="noturno">Noturno</option>
                            <option value="integral">Integral</option>
                          </select>
                        </div>
                      </div>

                      <div className="mb-3">
                        <input
                          type="date"
                          className="form-control"
                          placeholder="Previsão de conclusão"
                          value={previsaoConclusao}
                          onChange={(e) => setPrevisaoConclusao(e.target.value)}
                        />
                        <small className="form-text text-muted">
                          Previsão de conclusão do curso (opcional)
                        </small>
                      </div>

                      <div className="mb-3">
                        <textarea
                          className="form-control"
                          rows={3}
                          placeholder="Observações (opcional)"
                          value={observacoes}
                          onChange={(e) => setObservacoes(e.target.value)}
                        />
                        <small className="form-text text-muted">
                          Informações adicionais sobre seu estágio ou curso
                        </small>
                      </div>

                      <div className="row">
                        <div className="col-6">
                          <button type="button" className="btn btn-secondary btn-block" onClick={voltarEtapa}>
                            <i className="fas fa-arrow-left mr-2"></i> Voltar
                          </button>
                        </div>
                        <div className="col-6">
                          <button type="submit" className="btn btn-primary btn-block">
                            Continuar <i className="fas fa-arrow-right ml-2"></i>
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  {/* ETAPA 3: Dados de Login */}
                  {etapa === 3 && (
                    <>
                      <h5 className="mb-3">Dados de Acesso ao Sistema</h5>
                      <p className="text-muted small mb-4">
                        <i className="fas fa-info-circle mr-1"></i>
                        Defina suas credenciais para acessar o sistema
                      </p>

                      <div className="mb-3">
                        <label className="form-label small text-muted mb-1" htmlFor="cadastroEstudanteEmpresaLogin">
                          <i className="fas fa-building me-1"></i>
                          Nome da Clínica/Prática (para login)
                        </label>
                        <div className="input-group">
                          <input
                            id="cadastroEstudanteEmpresaLogin"
                            type="text"
                            className={`form-control ${erros.empresaLogin ? 'is-invalid' : ''}`}
                            placeholder="Ex: Clinica Estagio Maria Silva"
                            value={empresaLogin}
                            onChange={(e) => setEmpresaLogin(e.target.value)}
                            required
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-building"></i>
                            </div>
                          </div>
                        </div>
                        <small className="form-text text-muted">
                          💡 Nome simples para identificar sua clínica/prática no login. Este será o nome da sua área de trabalho no sistema.
                        </small>
                      </div>

                      <div className="mb-3">
                        <label className="form-label small text-muted mb-1" htmlFor="cadastroEstudanteNomeUsuario">
                          <i className="fas fa-user me-1"></i>
                          Nome de Usuário (para login)
                        </label>
                        <div className="input-group">
                          <input
                            id="cadastroEstudanteNomeUsuario"
                            type="text"
                            className={`form-control ${erros.nomeUsuario ? 'is-invalid' : ''}`}
                            placeholder="Ex: maria.silva"
                            value={nomeUsuario}
                            onChange={(e) => setNomeUsuario(e.target.value)}
                            required
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-at"></i>
                            </div>
                          </div>
                        </div>
                        <small className="form-text text-muted">
                          💡 Este será seu nome de usuário para fazer login no sistema
                        </small>
                      </div>

                      <div className="mb-3">
                        <label className="form-label small text-muted mb-1" htmlFor="cadastroEstudanteSenha">
                          <i className="fas fa-lock me-1"></i>
                          Senha
                        </label>
                        <div className="input-group">
                          <input
                            id="cadastroEstudanteSenha"
                            type="password"
                            className={`form-control ${erros.senha ? 'is-invalid' : ''}`}
                            placeholder="Mínimo 6 caracteres"
                            value={senha}
                            onChange={(e) => setSenha(e.target.value)}
                            required
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-lock"></i>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label small text-muted mb-1" htmlFor="cadastroEstudanteConfirmaSenha">
                          <i className="fas fa-lock me-1"></i>
                          Confirmar Senha
                        </label>
                        <div className="input-group">
                          <input
                            id="cadastroEstudanteConfirmaSenha"
                            type="password"
                            className={`form-control ${erros.confirmaSenha ? 'is-invalid' : ''}`}
                            placeholder="Digite a senha novamente"
                            value={confirmaSenha}
                            onChange={(e) => setConfirmaSenha(e.target.value)}
                            required
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">
                              <i className="fas fa-lock"></i>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="alert alert-info">
                        <i className="fas fa-lightbulb mr-2"></i>
                        <strong>Importante:</strong> Guarde bem essas informações! Você precisará delas para acessar o sistema.
                      </div>

                      <div className="row">
                        <div className="col-6">
                          <button type="button" className="btn btn-secondary btn-block" onClick={voltarEtapa}>
                            <i className="fas fa-arrow-left mr-2"></i> Voltar
                          </button>
                        </div>
                        <div className="col-6">
                          <button 
                            type="submit" 
                            className="btn btn-primary btn-block" 
                            disabled={carregando}
                          >
                            {carregando ? (
                              <>
                                <span className="spinner-border spinner-border-sm mr-2"></span>
                                Criando...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-user-plus mr-2"></i>
                                Criar Conta
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </form>

                <div className="text-center mt-3">
                  <p>
                    Já tem uma conta?{' '}
                    <Link to="/login" className="text-primary fw-bold">
                      Faça login
                    </Link>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
